import { isMultiDay } from "@/components/calendar/multiday";
import { chipColor } from "@/lib/apptDisplay";
import { ServiceColorDots } from "@/components/calendar/ServiceColors";
import { colorSwatchHex } from "@/lib/vehicleCatalog";

const CELL_MIN_H = 118;
const MAX_VISIBLE = 3;
const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const WEEKDAYS_FULL = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];
const WEEKDAY_INITIALS = ["M", "D", "M", "D", "F", "S", "S"];

function startOfMonthGrid(d) {
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const day = (first.getDay() + 6) % 7;
  first.setDate(first.getDate() - day);
  first.setHours(0, 0, 0, 0);
  return first;
}

function startOfWeek(d) {
  const first = new Date(d);
  const day = (first.getDay() + 6) % 7;
  first.setDate(first.getDate() - day);
  first.setHours(0, 0, 0, 0);
  return first;
}

function isoWeekNumber(value) {
  const d = new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()));
  const weekday = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - weekday);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

function dayStart(value) {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
}

function inclusiveEndDay(appointment) {
  return dayStart(new Date(new Date(appointment.end).getTime() - 1));
}

function eventsForDay(day, appointments) {
  const start = dayStart(day);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return appointments
    .filter((appointment) => new Date(appointment.end) > start && new Date(appointment.start) < end)
    .sort((a, b) => {
      const multiDifference = Number(isMultiDay(b)) - Number(isMultiDay(a));
      return multiDifference || new Date(a.start) - new Date(b.start);
    });
}

function MonthAppointment({ appointment, day, weekIndex, onApptClick, isSelected }) {
  const start = new Date(appointment.start);
  const end = new Date(appointment.end);
  const startHM = start.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  const endHM = end.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  const multiDay = isMultiDay(appointment);
  const currentDay = dayStart(day);
  const appointmentStartDay = dayStart(start);
  const appointmentEndDay = inclusiveEndDay(appointment);
  const continuesLeft = multiDay && appointmentStartDay < currentDay;
  const continuesRight = multiDay && appointmentEndDay > currentDay;
  const isBegut = appointment.terminart === "begutachtung";
  const chip = chipColor(appointment);
  const vehicle = (appointment.car_model || "").trim();
  const plate = (appointment.plate || "").trim();
  const service = (appointment.service_name || appointment.services?.[0]?.service_name || "").trim();
  const primary = isBegut ? "Begutachtung" : (vehicle || service || appointment.customer_name || "Termin");
  const secondary = [
    appointment.vehicle_year ? `Bj. ${appointment.vehicle_year}` : "",
    plate,
    service && service !== primary ? service : "",
    appointment.customer_name && appointment.customer_name !== primary ? appointment.customer_name : "",
  ].filter(Boolean).join(" · ");
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onApptClick?.(appointment);
      }}
      data-testid={multiDay ? `month-w${weekIndex}-multiday-${appointment.id}` : `month-appt-${appointment.id}`}
      title="Termin auswählen und Aktionen anzeigen"
      className={[
        "stellar-month-chip relative w-full text-left hover:brightness-[0.98] overflow-hidden",
        multiDay ? "stellar-month-chip-multiday" : "rounded-md",
        continuesLeft ? "stellar-month-continues-left" : "",
        continuesRight ? "stellar-month-continues-right" : "",
        isSelected ? "stellar-month-chip-selected" : "",
      ].join(" ")}
      style={{
        borderLeft: continuesLeft ? "none" : `4px solid ${chip}`,
        backgroundColor: `color-mix(in srgb, ${chip} ${multiDay ? 30 : 20}%, hsl(var(--card)))`,
        borderTopLeftRadius: continuesLeft ? 0 : 7,
        borderBottomLeftRadius: continuesLeft ? 0 : 7,
        borderTopRightRadius: continuesRight ? 0 : 7,
        borderBottomRightRadius: continuesRight ? 0 : 7,
      }}
    >
      <ServiceColorDots appointment={appointment} className="stellar-month-service-squares" />
      <div className="stellar-month-chip-main truncate">
        <span className="font-mono tabular-nums font-semibold text-foreground">
          {continuesLeft ? "↳ " : startHM}
          {!multiDay && <><span className="text-muted-foreground">–</span>{endHM}</>}
        </span>
        <span className={`ml-1.5 ${isBegut ? "italic text-muted-foreground" : "font-semibold"}`}>{primary}</span>
        {multiDay && continuesRight && <span className="ml-1 text-muted-foreground">→</span>}
      </div>
      {(secondary || appointment.vehicle_color) && <div className="stellar-month-chip-secondary flex items-center gap-1 truncate text-muted-foreground">
        {appointment.vehicle_color && <span className="h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-black/10" style={{ background: colorSwatchHex(appointment.vehicle_color) }} title={appointment.vehicle_color} />}
        <span className="truncate">{secondary}</span>
      </div>}
      {multiDay && !continuesRight && (
        <div className="stellar-month-chip-secondary truncate font-mono text-muted-foreground">Ende {endHM}</div>
      )}
    </button>
  );
}

function WeekRow({
  wi, weekDays, appointments, currentMonth, today, onApptClick, onDayClick,
  onMoveDate, movingAppointment, onCancelMove, multiWeek,
}) {
  const chooseDay = (day) => {
    if (!movingAppointment) {
      onDayClick?.(day);
      return;
    }
    const accepted = onMoveDate?.(movingAppointment, day);
    if (accepted !== false) onCancelMove();
  };

  return (
    <div className="stellar-month-week relative border-b border-border/60 last:border-b-0" data-testid={`month-week-${wi}`}>
      {multiWeek && <div className="stellar-multiweek-kw">KW {isoWeekNumber(weekDays[0])}</div>}
      <div className="grid h-full grid-cols-7 gap-px bg-border">
        {weekDays.map((day, dayIndex) => {
          const inMonth = multiWeek || day.getMonth() === currentMonth;
          const isToday = day.getTime() === today.getTime();
          const isWeekend = day.getDay() === 0 || day.getDay() === 6;
          const dayEvents = eventsForDay(day, appointments);
          const shown = dayEvents.slice(0, MAX_VISIBLE);
          const hidden = Math.max(0, dayEvents.length - shown.length);
          const isOrigin = movingAppointment && dayStart(movingAppointment.start).getTime() === dayStart(day).getTime();

          return (
            <div
              key={dayIndex}
              role="button"
              tabIndex={0}
              onClick={() => chooseDay(day)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  chooseDay(day);
                }
              }}
              data-testid={`month-day-${day.toISOString().slice(0, 10)}`}
              className={[
                "stellar-month-day relative text-left transition-colors flex flex-col",
                isToday ? "stellar-month-today" : (isWeekend ? "bg-amber-50/70 dark:bg-amber-900/10" : "bg-card"),
                inMonth ? "" : "stellar-month-outside",
                movingAppointment ? "stellar-month-move-target" : "hover:bg-muted/60",
                isOrigin ? "stellar-month-move-origin" : "",
              ].join(" ")}
              style={{ minHeight: CELL_MIN_H }}
            >
              <div className="stellar-month-date">
                {multiWeek && day.getDate() === 1 && (
                  <span className="stellar-multiweek-month">{day.toLocaleDateString("de-DE", { month: "short" })}</span>
                )}
                <span className={`stellar-month-date-number ${isToday ? "stellar-month-date-today" : ""}`}>{day.getDate()}</span>
              </div>
              <div
                className="stellar-month-appointments"
                data-count={shown.length}
                style={{ gridTemplateRows: `repeat(${Math.max(1, shown.length)}, minmax(0, 1fr))` }}
              >
                {shown.map((appointment) => (
                  <MonthAppointment
                    key={appointment.id}
                    appointment={appointment}
                    day={day}
                    weekIndex={wi}
                    onApptClick={onApptClick}
                    isSelected={movingAppointment?.id === appointment.id}
                  />
                ))}
                {hidden > 0 && <div className="stellar-month-more">+{hidden} weitere</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function MonthView({
  date, appointments, onApptClick, onDayClick, onMoveDate, movingAppointment, onCancelMove,
  multiWeek = false,
}) {
  const start = multiWeek ? startOfWeek(date) : startOfMonthGrid(date);
  const currentMonth = date.getMonth();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const weeks = Array.from({ length: multiWeek ? 4 : 6 }, (_, weekIndex) =>
    Array.from({ length: 7 }, (_, dayIndex) => {
      const day = new Date(start);
      day.setDate(day.getDate() + weekIndex * 7 + dayIndex);
      return day;
    })
  );

  return (
    <div className={`stellar-month-view p-2 sm:p-3 md:p-4 ${multiWeek ? "stellar-multiweek-view" : ""}`}>
      <div className="stellar-month-weekdays grid grid-cols-7 mb-1 sm:mb-2">
        {(multiWeek ? WEEKDAYS_FULL : WEEKDAYS).map((weekday, index) => (
          <div key={`${weekday}-${index}`} className="text-[10px] uppercase tracking-widest text-muted-foreground text-center py-1 sm:py-2">
            {multiWeek ? (
              <><span className="hidden sm:inline">{weekday}</span><span className="sm:hidden font-bold">{WEEKDAY_INITIALS[index]}</span></>
            ) : weekday}
          </div>
        ))}
      </div>
      <div className="stellar-month-grid rounded-md overflow-hidden border border-border/60 bg-border">
        {weeks.map((weekDays, weekIndex) => (
          <WeekRow
            key={weekIndex}
            wi={weekIndex}
            weekDays={weekDays}
            appointments={appointments}
            currentMonth={currentMonth}
            today={today}
            onApptClick={onApptClick}
            onDayClick={onDayClick}
            onMoveDate={onMoveDate}
            movingAppointment={movingAppointment}
            onCancelMove={onCancelMove}
            multiWeek={multiWeek}
          />
        ))}
      </div>
    </div>
  );
}
